import React, { useEffect, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons';
import useFrustrationSignalData from '../hooks/useFrustrationSignalData';
import useFetchDodFrustrationSignalData from '../hooks/useFetchDodFrustrationSignalData';
import humanizeUtil from '@src/utils/humanizeUtil';
import tracker from '@src/utils/sensor/apptracker';
import {
  Row,
  Cell,
  FullCell,
  TrendValue,
  CellNumber,
  CellPrimary,
  CellSecondary,
  DeleteButtonContainer,
  DeleteButton,
} from './styled';
import { Retry } from '@src/components/common';
import { get } from 'lodash';
import ErrorBoundary from '@src/components/common/Retry/ErrorBoundary';
import {
  appliedFilterAtom,
  appliedIntervalAtom,
  enableDodAtom,
  removeFrustrationSignalAtom,
} from '../../atoms';
import {
  openFrustrationSignalPreset,
  generateFrustrationSignalPresetUrl,
  generateFrustrationSignalPresetUrlWithStorage,
} from '../../drillDown';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { shouldUseBrowserDefaultNavigation } from '../../utils';

const RowData = ({ signalMeta, nameColumnWidth, columnWidth, data, onDataUpdate, dodData }) => {
  const appliedFilter = useAtomValue(appliedFilterAtom);
  const appliedInterval = useAtomValue(appliedIntervalAtom);
  const enableDod = useAtomValue(enableDodAtom);
  const navigate = useNavigate();
  const signalMetrics = signalMeta.metrics || {};

  const withUserPctMetricKey = getPercentageMetricByKey({ metricKey: signalMetrics.with_user });
  const withSessionPctMetricKey = getPercentageMetricByKey({
    metricKey: signalMetrics.with_session,
  });

  const countData = get(data, ['totals', signalMetrics.count], null);
  const withUserData = get(data, ['totals', signalMetrics.with_user], null);
  const withSessionData = get(data, ['totals', signalMetrics.with_session], null);
  const withUserDataPercentage = get(data, ['totals', withUserPctMetricKey], null);
  const withSessionDataPercentage = get(data, ['totals', withSessionPctMetricKey], null);

  // Get DoD totals for trend indicators
  const countDodData = enableDod ? get(dodData, ['totals', signalMetrics.count], null) : null;
  const withSessionDodData = enableDod
    ? get(dodData, ['totals', signalMetrics.with_session], null)
    : null;
  const withUserDodData = enableDod
    ? get(dodData, ['totals', signalMetrics.with_user], null)
    : null;

  // Report the impacted sessions value for sorting
  useEffect(() => {
    if (
      onDataUpdate &&
      withSessionDataPercentage !== null &&
      withSessionDataPercentage !== undefined
    ) {
      onDataUpdate((prev) => ({
        ...prev,
        [signalMeta.id]: withSessionDataPercentage,
      }));
    }
  }, [withSessionDataPercentage, signalMeta.id, onDataUpdate]);

  const countHumanized = humanizeUtil(countData, signalMetrics.count);
  const withUserHumanized = humanizeUtil(withUserData, signalMetrics.with_user);
  const withSessionHumanized = humanizeUtil(withSessionData, signalMetrics.with_session);
  const withUserHumanizedPercentage = humanizeUtil(withUserDataPercentage, withUserPctMetricKey);
  const withSessionHumanizedPercentage = humanizeUtil(
    withSessionDataPercentage,
    withSessionPctMetricKey,
  );

  // Calculate percentage changes for trend indicators
  const calculatePercentageChange = (current: number | null, previous: number | null) => {
    if (current === null || previous === null || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  const countPercentageChange = useMemo(
    () => calculatePercentageChange(countData, countDodData),
    [countData, countDodData],
  );

  const sessionPercentageChange = useMemo(
    () => calculatePercentageChange(withSessionData, withSessionDodData),
    [withSessionData, withSessionDodData],
  );

  const userPercentageChange = useMemo(
    () => calculatePercentageChange(withUserData, withUserDodData),
    [withUserData, withUserDodData],
  );

  const countPercentageText =
    countPercentageChange !== null && countPercentageChange !== 0
      ? `${countPercentageChange > 0 ? '+' : ''}${countPercentageChange.toFixed(1)}%`
      : countPercentageChange === 0
        ? '0%'
        : '--';

  // For frustration signals count: more is bad (red), less is good (green)
  const isCountIncrease = countPercentageChange !== null && countPercentageChange > 0;
  const isCountNeutral = countPercentageChange === 0 || countPercentageChange === null;

  // For frustration signals: more is bad (red), less is good (green)
  // So: increase (positive change) = red with +, decrease (negative change) = green with -
  const sessionPercentageText =
    sessionPercentageChange !== null && sessionPercentageChange !== 0
      ? `${sessionPercentageChange > 0 ? '+' : ''}${sessionPercentageChange.toFixed(1)}%`
      : sessionPercentageChange === 0
        ? '0%'
        : '--';

  const userPercentageText =
    userPercentageChange !== null && userPercentageChange !== 0
      ? `${userPercentageChange > 0 ? '+' : ''}${userPercentageChange.toFixed(1)}%`
      : userPercentageChange === 0
        ? '0%'
        : '--';

  // Color: red if increased (more is bad), green if decreased (less is good), grey if no change or no value
  const isSessionIncrease = sessionPercentageChange !== null && sessionPercentageChange > 0;
  const isUserIncrease = userPercentageChange !== null && userPercentageChange > 0;
  const isSessionNeutral = sessionPercentageChange === 0 || sessionPercentageChange === null;
  const isUserNeutral = userPercentageChange === 0 || userPercentageChange === null;

  const signalUrl = generateFrustrationSignalPresetUrl({ signalMeta }) || '#';

  const onFrustrationSignalClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Allow browser default behavior for Ctrl/Cmd+Click, middle-click, etc.
    if (shouldUseBrowserDefaultNavigation(e)) {
      return;
    }

    // For normal clicks, prevent default and navigate in same tab
    e.preventDefault();
    tracker('Click preset link from Overview Table Frustration Signal', {
      frustrationSignalId: signalMeta.id,
    });
    openFrustrationSignalPreset({ signalMeta, appliedFilter, appliedInterval, navigate });
  };

  const onFrustrationSignalContextMenu = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const url = generateFrustrationSignalPresetUrlWithStorage({
      signalMeta,
      appliedFilter,
      appliedInterval,
    });
    if (url) {
      tracker('Right-click preset link from Overview Table Frustration Signal', {
        frustrationSignalId: signalMeta.id,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Row
      href={signalUrl}
      onClick={onFrustrationSignalClick}
      onContextMenu={onFrustrationSignalContextMenu}
    >
      <FullCell $width={nameColumnWidth}>{signalMeta.name}</FullCell>
      <Cell $width={columnWidth}>
        <CellPrimary>
          {countHumanized.humanizedNumber} {countHumanized.humanizedUnit}
        </CellPrimary>
        {enableDod && (
          <TrendValue $positive={!isCountIncrease} $neutral={isCountNeutral}>
            {!isCountNeutral && (countPercentageChange! > 0 ? <RiseOutlined /> : <FallOutlined />)}
            {countPercentageText}
          </TrendValue>
        )}
      </Cell>
      <Cell $width={columnWidth}>
        <CellNumber>
          <CellPrimary>
            {withSessionHumanizedPercentage.humanizedNumber}{' '}
            {withSessionHumanizedPercentage.humanizedUnit}
          </CellPrimary>
          <CellSecondary>
            {withSessionHumanized.humanizedNumber} {withSessionHumanized.humanizedUnit}
          </CellSecondary>
        </CellNumber>
        {enableDod && (
          <TrendValue $positive={!isSessionIncrease} $neutral={isSessionNeutral}>
            {!isSessionNeutral &&
              (sessionPercentageChange! > 0 ? <RiseOutlined /> : <FallOutlined />)}
            {sessionPercentageText}
          </TrendValue>
        )}
      </Cell>
      <Cell $width={columnWidth}>
        <CellNumber>
          <CellPrimary>
            {withUserHumanizedPercentage.humanizedNumber}{' '}
            {withUserHumanizedPercentage.humanizedUnit}
          </CellPrimary>
          <CellSecondary>
            {withUserHumanized.humanizedNumber} {withUserHumanized.humanizedUnit}
          </CellSecondary>
        </CellNumber>
        {enableDod && (
          <TrendValue $positive={!isUserIncrease} $neutral={isUserNeutral}>
            {!isUserNeutral && (userPercentageChange! > 0 ? <RiseOutlined /> : <FallOutlined />)}
            {userPercentageText}
          </TrendValue>
        )}
      </Cell>
    </Row>
  );
};

const DeletedSignalRow = ({ signalMeta, nameColumnWidth, columnWidth }) => {
  const removeSignal = useSetAtom(removeFrustrationSignalAtom);

  const handleRemoveSignal = () => {
    removeSignal(String(signalMeta.id));
  };

  return (
    <Row as="div">
      <FullCell $width={nameColumnWidth} title={signalMeta.name}>
        This frustration signal has been deleted
      </FullCell>
      <Cell $width={columnWidth} />
      <Cell $width={columnWidth} />
      <Cell $width={columnWidth}>
        <DeleteButtonContainer style={{ width: '100%' }}>
          <DeleteButton
            type="text"
            size="large"
            icon={<DeleteOutlined />}
            onClick={handleRemoveSignal}
            danger
            title="Remove this signal from the dashboard"
          />
        </DeleteButtonContainer>
      </Cell>
    </Row>
  );
};

const ActiveSignalRow = ({
  signalMeta,
  nameColumnWidth,
  columnWidth,
  query,
  onDataUpdate,
  dodQuery,
}) => {
  return (
    <ErrorBoundary>
      <Retry
        progress
        isLoading={query.isFetching}
        showError={query.isError}
        fetchData={query.refetch}
        queued={query.queued}
        horizontal={true}
        compact={true}
      >
        <RowData
          signalMeta={signalMeta}
          nameColumnWidth={nameColumnWidth}
          columnWidth={columnWidth}
          data={query.data}
          onDataUpdate={onDataUpdate}
          dodData={dodQuery?.data}
        />
      </Retry>
    </ErrorBoundary>
  );
};

const ActiveSignalRowContainer = ({ signalMeta, nameColumnWidth, columnWidth, onDataUpdate }) => {
  const signalId = signalMeta?.id || '';
  const enableDod = useAtomValue(enableDodAtom);
  const query = useFrustrationSignalData({ signalId, signalMeta });

  // Fetch DoD data when enabled
  const dodQuery = useFetchDodFrustrationSignalData({
    signalId,
    signalMeta,
    enabled: enableDod,
  });

  return (
    <ActiveSignalRow
      signalMeta={signalMeta}
      nameColumnWidth={nameColumnWidth}
      columnWidth={columnWidth}
      query={query}
      onDataUpdate={onDataUpdate}
      dodQuery={enableDod ? dodQuery : null}
    />
  );
};

export const RowComponent = ({ signalMeta, nameColumnWidth, columnWidth, onDataUpdate }) => {
  if (signalMeta?.isDeleted) {
    return (
      <DeletedSignalRow
        signalMeta={signalMeta}
        nameColumnWidth={nameColumnWidth}
        columnWidth={columnWidth}
      />
    );
  }

  return (
    <ActiveSignalRowContainer
      signalMeta={signalMeta}
      nameColumnWidth={nameColumnWidth}
      columnWidth={columnWidth}
      onDataUpdate={onDataUpdate}
    />
  );
};
